import { UseQueryOptions, useQuery, keepPreviousData } from '@tanstack/react-query';
import { getPartners } from '@apis/partner';
import type { PageResponse } from '@models/api';
import type { Partner, PartnersSearchForm } from '@models/partner';
import { dropEmptyValues } from '../../utils/common';

export const usePartnerList = ({
  page,
  pageSize,
  searchParam,
  includeDeleted = false,
}: {
  page: number;
  pageSize: number;
  searchParam: PartnersSearchForm;
  includeDeleted?: boolean;
}) => {
  /**
   * Build stable, minimal params for:
   * - queryKey (cache separation by filter)
   * - queryFn (API request params)
   *
   * dropEmptyValues should trim strings and remove empty values.
   */
  const cleanedParams = dropEmptyValues(searchParam, [
    'name',
    'manager',
    'email',
    'address',
  ]);

  const queryOptions: UseQueryOptions<PageResponse<Partner>, Error> = {
    queryKey: ['partners', page, pageSize, cleanedParams, includeDeleted],
    queryFn: () =>
      getPartners({
        pageNo: page,
        pageSize,
        ...cleanedParams,
        includeDeleted: includeDeleted || undefined,
      }),
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
    enabled: page > 0 && pageSize > 0,
  };

  return useQuery(queryOptions);
};
